import { Row } from "@tanstack/react-table";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IProductAssignment } from "@/interfaces/tools.interface";
import { cn, renderFullName } from "@/lib/utils";

type TUseAwaitingRequestColumns = {
  getActionOptions: (row: Row<IProductAssignment>) => ActionButtonDataTypes[];
};

export const useAwaitingRequestColumns = ({
  getActionOptions,
}: TUseAwaitingRequestColumns): ExtendedColumnDef<IProductAssignment>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (value) => value?.product?.name ?? "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.product?.images[0]}
            name={`${row.original?.product?.name}`}
            title={`${row.original?.product?.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Quantity",
        dataTransformer: (value) =>
          value?.assignedQuantity
            ? value?.assignedQuantity.toString()
            : (value?.subProductAssignments.length?.toString() ?? "-"),
      },
      accessorKey: "Quantity",
      header: "Quantity",
      cell: ({ row }) =>
        row.original?.assignedQuantity
          ? row.original?.assignedQuantity.toString()
          : (row.original?.subProductAssignments.length?.toString() ?? "-"),
      meta: {
        minWidth: 100,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Sub Products",
        dataTransformer: (value) =>
          value?.subProductAssignments.length
            ? value?.subProductAssignments
                ?.map((subProduct) => subProduct?.subProduct?.name)
                .join(", ")
            : "-",
      },
      accessorKey: "SubProducts",
      header: "Sub Products",
      cell: ({ row }) =>
        row.original?.subProductAssignments.length
          ? row.original?.subProductAssignments
              ?.map((subProduct) => subProduct?.subProduct?.name)
              .join(", ")
          : "-",
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Assigned By",
        dataTransformer: (value) =>
          renderFullName({
            params: {
              firstName: value?.assignedBy?.firstName,
              lastName: value?.assignedBy?.lastName,
            },
          }),
      },
      accessorKey: "Assigned By",
      header: "Assigned By",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.assignedBy?.image}
            name={`${renderFullName({
              params: row.original.assignedBy || {},
            })}`}
            title={`${renderFullName({
              params: row.original.assignedBy || {},
            })}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        minWidth: 200,
      },
    },
    {
      id: "actions",
      cell: ({ row }): React.JSX.Element => {
        return (
          <>
            <CustomDropdown data={getActionOptions(row)} />
          </>
        );
      },
      meta: {
        classNameRow: "w-14",
      },
    },
  ];
};
